import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  HeldEmailFilters,
  SystemEmailRegistryEntry,
  SystemEmailRegistryFilters,
  systemEmailsService,
  UpdateSystemEmailModeRequest,
} from "../../services/admin/systemEmails.service";

interface HttpError {
  response?: { status?: number; data?: { currentRevision?: number } };
}

export class SystemEmailRevisionConflictError extends Error {
  readonly currentRevision?: number;

  constructor(currentRevision?: number) {
    super(
      "This email setting changed while you were editing. Reload and try again."
    );
    this.name = "SystemEmailRevisionConflictError";
    this.currentRevision = currentRevision;
  }
}

export function translateSystemEmailError(error: unknown): Error {
  const response = (error as HttpError).response;
  if (response?.status === 409) {
    return new SystemEmailRevisionConflictError(response.data?.currentRevision);
  }
  if (response?.status === 403) {
    return new Error("You do not have permission to manage system emails.");
  }
  if (response?.status === 404) {
    return new Error("That system email type could not be found.");
  }
  return new Error("System email settings are unavailable. Please try again.");
}

export function translateHeldEmailError(error: unknown): Error {
  const status = (error as HttpError).response?.status;
  if (status === 400) return new Error("Select at least one queued email.");
  if (status === 403)
    return new Error("You do not have permission to release queued emails.");
  if (status === 404) return new Error("That queued email could not be found.");
  if (status === 409)
    return new Error("That email was already processed by someone else.");
  return new Error("Pending emails are unavailable. Please try again.");
}

export const SYSTEM_EMAILS_QUERY_KEY = ["system-emails"] as const;

export function useSystemEmailRegistry(
  filters: SystemEmailRegistryFilters = {}
) {
  return useQuery({
    queryKey: [...SYSTEM_EMAILS_QUERY_KEY, "registry", filters],
    queryFn: async () => {
      try {
        return await systemEmailsService.getRegistry(filters);
      } catch (error) {
        throw translateSystemEmailError(error);
      }
    },
  });
}

export function useSystemEmailPreview(emailTypeId: string | null) {
  return useQuery({
    queryKey: [...SYSTEM_EMAILS_QUERY_KEY, "preview", emailTypeId],
    queryFn: async () => {
      try {
        return await systemEmailsService.getPreview(emailTypeId as string);
      } catch (error) {
        throw translateSystemEmailError(error);
      }
    },
    enabled: Boolean(emailTypeId),
  });
}

export function useUpdateSystemEmailMode() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (request: UpdateSystemEmailModeRequest) => {
      try {
        return await systemEmailsService.updateMode(request);
      } catch (error) {
        throw translateSystemEmailError(error);
      }
    },
    onSuccess: (entry: SystemEmailRegistryEntry) => {
      queryClient.setQueriesData<SystemEmailRegistryEntry[]>(
        { queryKey: [...SYSTEM_EMAILS_QUERY_KEY, "registry"] },
        (entries) =>
          entries?.map((item) => (item.id === entry.id ? entry : item))
      );
    },
  });
}

export function useHeldEmails(filters: HeldEmailFilters = {}) {
  return useQuery({
    queryKey: [...SYSTEM_EMAILS_QUERY_KEY, "held", filters],
    queryFn: async () => {
      try {
        return await systemEmailsService.getHeldEmails(filters);
      } catch (error) {
        throw translateHeldEmailError(error);
      }
    },
  });
}

export function useHeldEmailStats() {
  return useQuery({
    queryKey: [...SYSTEM_EMAILS_QUERY_KEY, "held-stats"],
    queryFn: async () => {
      try {
        return await systemEmailsService.getHeldEmailStats();
      } catch (error) {
        throw translateHeldEmailError(error);
      }
    },
  });
}

export function useHeldEmail(heldEmailId: string | null) {
  return useQuery({
    queryKey: [...SYSTEM_EMAILS_QUERY_KEY, "held-detail", heldEmailId],
    queryFn: async () => {
      try {
        return await systemEmailsService.getHeldEmail(heldEmailId as string);
      } catch (error) {
        throw translateHeldEmailError(error);
      }
    },
    enabled: Boolean(heldEmailId),
  });
}

function useHeldEmailMutation(action: "release" | "cancel") {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (heldEmailIds: string[]) => {
      try {
        return action === "release"
          ? await systemEmailsService.releaseHeldEmails(heldEmailIds)
          : await systemEmailsService.cancelHeldEmails(heldEmailIds);
      } catch (error) {
        throw translateHeldEmailError(error);
      }
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: SYSTEM_EMAILS_QUERY_KEY,
      });
    },
  });
}

export function useReleaseHeldEmails() {
  return useHeldEmailMutation("release");
}

export function useCancelHeldEmails() {
  return useHeldEmailMutation("cancel");
}
